/**
 * Customization picker. Opens when a teacher taps a menu item.
 * Rebuilds local state on `item` change so reusing the same modal for
 * different items doesn't leak prior selections.
 *
 * Supports conditional groups via `dependsOn` — a group only renders
 * (and its required-ness only counts) if the parent group has one of
 * the listed options selected. Hidden groups' selections do NOT flow
 * into the line total. Used by Build Your Own to branch on drink type.
 */
import { useEffect, useState } from 'react';
import type { CustomizationGroup, MenuItem, SelectedCustomization } from '@cracked-mug/shared';
import { computeLineTotal, formatPrice } from '@cracked-mug/shared';
import { Minus, Plus, X } from 'lucide-react';
import { cn } from '../../lib/cn';
import { useCartStore } from '../../store/cartStore';
import { pushToast } from '../../store/toastStore';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';

interface ItemModalProps {
  item: MenuItem | null;
  onClose: () => void;
}

type Selections = Map<string, string[]>;

function initialSelections(item: MenuItem): Selections {
  const map: Selections = new Map();
  for (const group of item.customizationGroups) {
    const defaults = group.options.filter((o) => o.isDefault).map((o) => o.optionId);
    if (defaults.length === 0 && group.required && group.type === 'single' && group.options[0]) {
      map.set(group.groupId, [group.options[0].optionId]);
    } else {
      map.set(group.groupId, defaults);
    }
  }
  return map;
}

/**
 * Whether a group is currently visible given the selections. Groups with
 * no `dependsOn` are always visible; groups with one are visible when the
 * parent group has at least one of the configured option ids selected.
 */
function isGroupVisible(group: CustomizationGroup, selections: Selections): boolean {
  if (!group.dependsOn) return true;
  const parentSelection = selections.get(group.dependsOn.groupId) ?? [];
  return group.dependsOn.optionIds.some((id) => parentSelection.includes(id));
}

function buildSelectedCustomizations(
  groups: CustomizationGroup[],
  selections: Selections,
): SelectedCustomization[] {
  return groups
    .filter((g) => isGroupVisible(g, selections))
    .map<SelectedCustomization | null>((group) => {
      const optionIds = selections.get(group.groupId) ?? [];
      if (optionIds.length === 0) return null;
      const selectedOptions = group.options.filter((o) => optionIds.includes(o.optionId));
      if (selectedOptions.length === 0) return null;
      return {
        groupId: group.groupId,
        groupName: group.name,
        optionIds: selectedOptions.map((o) => o.optionId),
        optionLabels: selectedOptions.map((o) => o.label),
        priceDeltaCents: selectedOptions.reduce((s, o) => s + o.priceDeltaCents, 0),
      };
    })
    .filter((c): c is SelectedCustomization => c !== null);
}

function formatDelta(deltaCents: number): string {
  if (deltaCents === 0) return '';
  const sign = deltaCents > 0 ? '+' : '−';
  return `${sign}${formatPrice(Math.abs(deltaCents))}`;
}

export function ItemModal({ item, onClose }: ItemModalProps) {
  const [selections, setSelections] = useState<Selections>(new Map());
  const [quantity, setQuantity] = useState(1);
  const [specialInstructions, setSpecialInstructions] = useState('');
  const addItem = useCartStore((s) => s.addItem);

  useEffect(() => {
    if (item) {
      setSelections(initialSelections(item));
      setQuantity(1);
      setSpecialInstructions('');
    }
  }, [item]);

  if (!item) return null;

  const selectedCustomizations = buildSelectedCustomizations(item.customizationGroups, selections);
  const lineTotal = computeLineTotal(item.basePriceCents, selectedCustomizations, quantity);

  const handleSelectSingle = (groupId: string, optionId: string): void => {
    setSelections((prev) => {
      const next = new Map(prev);
      next.set(groupId, [optionId]);
      return next;
    });
  };

  const handleToggleMulti = (groupId: string, optionId: string): void => {
    setSelections((prev) => {
      const next = new Map(prev);
      const current = next.get(groupId) ?? [];
      const updated = current.includes(optionId)
        ? current.filter((id) => id !== optionId)
        : [...current, optionId];
      next.set(groupId, updated);
      return next;
    });
  };

  const visibleGroups = item.customizationGroups.filter((g) => isGroupVisible(g, selections));
  const canAdd = visibleGroups
    .filter((g) => g.required)
    .every((g) => (selections.get(g.groupId) ?? []).length > 0);

  const handleAddToCart = (): void => {
    addItem({
      itemId: item.itemId,
      itemName: item.name,
      imageUrl: item.imageUrl,
      basePriceCents: item.basePriceCents,
      quantity,
      selectedCustomizations,
      specialInstructions: specialInstructions.trim(),
    });
    pushToast({
      variant: 'success',
      title: 'Added to cart',
      description: `${String(quantity)} × ${item.name}`,
      durationMs: 2500,
    });
    onClose();
  };

  return (
    <Modal
      open={item !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={`Customize ${item.name}`}
      description={item.description}
      position="center"
    >
      <div className="flex items-start justify-between px-6 pt-6 pb-3 border-b border-wca-gray-light">
        <div className="min-w-0 pr-3">
          <h2 className="font-display text-2xl truncate">{item.name}</h2>
          <p className="text-sm text-wca-gray-mid mt-1 line-clamp-2">{item.description}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="p-2 text-wca-gray-mid hover:text-wca-black -mr-2 -mt-2"
        >
          <X size={20} />
        </button>
      </div>

      <div className="px-6 py-4 space-y-6 overflow-y-auto flex-1">
        {visibleGroups.map((group) => (
          <fieldset key={group.groupId}>
            <legend className="font-medium text-sm mb-2">
              {group.name}
              {group.required && <span className="text-wca-red ml-1">*</span>}
            </legend>
            <div className="flex flex-wrap gap-2">
              {group.options.map((option) => {
                const selected = (selections.get(group.groupId) ?? []).includes(option.optionId);
                const onClick =
                  group.type === 'single'
                    ? () => handleSelectSingle(group.groupId, option.optionId)
                    : () => handleToggleMulti(group.groupId, option.optionId);
                return (
                  <button
                    key={option.optionId}
                    type="button"
                    onClick={onClick}
                    aria-pressed={selected}
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-sm font-medium transition-colors min-h-[40px]',
                      selected
                        ? 'bg-wca-red text-white border-wca-red'
                        : 'bg-wca-surface text-wca-black border-wca-gray-light hover:border-wca-red',
                    )}
                  >
                    {option.label}
                    {option.priceDeltaCents !== 0 && (
                      <span className="font-mono text-xs opacity-80">
                        {formatDelta(option.priceDeltaCents)}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </fieldset>
        ))}

        <div>
          <label htmlFor="special-instructions" className="block font-medium text-sm mb-2">
            Special instructions
          </label>
          <textarea
            id="special-instructions"
            value={specialInstructions}
            onChange={(e) => setSpecialInstructions(e.target.value)}
            maxLength={280}
            rows={2}
            placeholder="Extra hot, light ice, etc."
            className="w-full rounded-lg bg-wca-gray-light p-3 focus:outline-none focus:ring-2 focus:ring-wca-red resize-none text-sm"
          />
        </div>

        <div className="flex items-center justify-between">
          <span className="font-medium text-sm">Quantity</span>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setQuantity((q) => Math.max(1, q - 1))}
              className="rounded-full bg-wca-gray-light w-9 h-9 flex items-center justify-center text-wca-black hover:bg-wca-gray-mid hover:text-white transition-colors"
              aria-label="Decrease quantity"
            >
              <Minus size={16} />
            </button>
            <span className="font-mono text-lg w-6 text-center">{quantity}</span>
            <button
              type="button"
              onClick={() => setQuantity((q) => Math.min(20, q + 1))}
              className="rounded-full bg-wca-gray-light w-9 h-9 flex items-center justify-center text-wca-black hover:bg-wca-gray-mid hover:text-white transition-colors"
              aria-label="Increase quantity"
            >
              <Plus size={16} />
            </button>
          </div>
        </div>
      </div>

      <div className="px-6 pb-6 pt-3 border-t border-wca-gray-light">
        <Button
          variant="primary"
          size="lg"
          className="w-full"
          disabled={!canAdd}
          onClick={handleAddToCart}
        >
          Add to cart · {formatPrice(lineTotal)}
        </Button>
      </div>
    </Modal>
  );
}
